import { useState } from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import KinerjaDropdown from '.'
import useSKP from 'app/KinerjaPage/hooks/useSKP'

import type {
  Kinerja,
  KinerjaSelection,
  KinerjaUtamaDetail,
} from 'types/kinerja'

interface KinerjaDropdownGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'className'> {
  skp?: Kinerja
  kinerjaType?:
    | 'praktikPembelajaran'
    | 'pengembanganKompetensi'
    | 'tugasTambahan'
  kinerja: KinerjaSelection[]
  originalKinerja?: KinerjaSelection[]
  content: KinerjaUtamaDetail[]
  onChange?: (value: KinerjaSelection[]) => void
  addButtonLabel?: string
  className?: string
  allowDeleteSingleValue?: boolean
  showDeleteConfirmation?: boolean
  showChanges?: boolean
  showPoint?: boolean
  offset?: number
  defaultUnselectedTargetQty?: string
  adjustmentInfoText?: string
  showCheckButton?: boolean
}

const EMPTY_VALUE: KinerjaSelection = {
  id: 0,
  kuantitas: 0,
}

export default function KinerjaDropdownGroup({
  skp,
  kinerjaType,
  kinerja,
  content,
  originalKinerja: topOriginalKinerja,
  onChange = () => {},
  addButtonLabel = 'Tambah',
  className = '',
  allowDeleteSingleValue = false,
  showDeleteConfirmation = false,
  showChanges = false,
  showPoint = false,
  offset = 0,
  defaultUnselectedTargetQty = '',
  adjustmentInfoText,
  showCheckButton,
  ...props
}: KinerjaDropdownGroupProps) {
  const {
    getChangesPraktikPembelajaran,
    getChangesPengembanganKompetensiByIndex,
    getChangesTugasTambahanByIndex,
  } = useSKP(skp)

  const getChangesFn = () => {
    switch (kinerjaType) {
      case 'praktikPembelajaran':
        return getChangesPraktikPembelajaran
      case 'pengembanganKompetensi':
        return getChangesPengembanganKompetensiByIndex
      default:
        return getChangesTugasTambahanByIndex
    }
  }

  const [values, setValues] = useState<KinerjaSelection[]>(
    kinerja.length > 0 ? kinerja : [EMPTY_VALUE]
  )

  const isDisabledAddButton = values.some((value) => !value.id)

  const isAllOptionsSelected = content
    .map((option) => option.id)
    .every((id) => {
      return values.map((value) => value.id).includes(id)
    })

  const removeValue = (index: number) => {
    const newValues = [...values]
    newValues.splice(index, 1)
    return newValues
  }

  const onDelete = (index: number) => {
    const newValues = removeValue(index)
    onChange(newValues)

    if (newValues.length === 0) {
      setValues([EMPTY_VALUE])
    } else {
      setValues(newValues)
    }
  }

  const addNewValue = () => {
    const newValues = [...values, EMPTY_VALUE]
    setValues(newValues)
  }

  const handleChange = (option: KinerjaSelection, index) => {
    const newValues = [...values]
    newValues[index] = option
    setValues(newValues)
    onChange(newValues)
  }

  return (
    <div className={clsx('flex flex-col gap-6', className)} {...props}>
      {values.map((value, index) => {
        const { originalNumber, originalKinerja, kinerja } = getChangesFn()(
          index,
          values,
          topOriginalKinerja
        )
        const selectedOption = content.find(
          (content) => content.id === value.id
        )
        const remainingOptions = content.filter(
          (content) => !values.map((value) => value.id).includes(content.id)
        )

        return (
          <KinerjaDropdown
            key={`KinerjaDropdown-${index}`}
            data-testid={`KinerjaDropdown-${index}`}
            options={[
              ...(selectedOption ? [selectedOption] : []),
              ...remainingOptions,
            ]}
            value={value}
            originalKinerjaNumber={offset + originalNumber}
            originalKinerja={originalKinerja}
            kinerjaNumber={offset + index + 1}
            kinerja={kinerja}
            onDelete={() => onDelete(index)}
            onChange={(option) => handleChange(option, index)}
            disableDeleteButton={
              values.length === 1 &&
              (allowDeleteSingleValue ? values[0].id === 0 : true)
            }
            showChanges={showChanges}
            showPoint={showPoint}
            showDeleteConfirmation={showDeleteConfirmation}
            defaultUnselectedTargetQty={defaultUnselectedTargetQty}
            adjustmentInfoText={adjustmentInfoText}
            showCheckButton={showCheckButton}
          />
        )
      })}

      <div>
        <Button
          color="white"
          disabled={isAllOptionsSelected || isDisabledAddButton}
          onClick={addNewValue}
        >
          <Icon className="mr-1">add_circle</Icon>
          {addButtonLabel}
        </Button>
      </div>
    </div>
  )
}
